import React, { useState, useEffect } from 'react';
import {
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  XCircle,
  TrendingUp,
  TrendingDown,
  Calendar,
  DollarSign,
  User,
  Truck,
  FileText,
  Filter,
  RefreshCw
} from 'lucide-react';
import { api } from '../services/authService';

const IntelligentAlertsPage = () => {
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    alert_type: '',
    severity: '',
    min_priority: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Carregar estatísticas
      const statsResponse = await api.get('/api/intelligent-alerts/stats');
      setStats(statsResponse.data.data);

      // Carregar alertas
      await loadAlerts();
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAlerts = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.alert_type) params.append('alert_type', filters.alert_type);
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.min_priority) params.append('min_priority', filters.min_priority);
      params.append('limit', '50');

      const response = await api.get(`/api/intelligent-alerts?${params}`);
      setAlerts(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar alertas:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    loadAlerts();
  };

  const generateAlerts = async () => {
    setLoading(true);
    try {
      const response = await api.post('/api/intelligent-alerts/generate', {});
      console.log('Alertas gerados:', response.data);
      alert(`Alertas gerados com sucesso! Total: ${response.data.total || 0}`);

      // Recarregar dados
      await loadData();
    } catch (error) {
      console.error('Erro ao gerar alertas:', error);
      alert('Erro ao gerar alertas. Verifique o console para mais detalhes.');
    } finally {
      setLoading(false);
    }
  };

  const resolveAlert = async (alertId) => {
    try {
      const action = prompt('Descreva a ação tomada para resolver este alerta:');
      if (!action) return;

      const userId = 1; // TODO: pegar do contexto de autenticação

      await api.post(`/api/intelligent-alerts/${alertId}/resolve`, {
        action_taken: action,
        user_id: userId
      });

      alert('Alerta resolvido com sucesso!');
      // Recarregar alertas
      await loadAlerts();
    } catch (error) {
      console.error('Erro ao resolver alerta:', error);
      alert('Erro ao resolver alerta. Tente novamente.');
    }
  };

  const dismissAlert = async (alertId) => {
    try {
      const reason = prompt('Motivo do descarte (opcional):');

      await api.post(`/api/intelligent-alerts/${alertId}/dismiss`, {
        reason: reason || 'Descartado manualmente'
      });

      alert('Alerta descartado com sucesso!');
      // Recarregar alertas
      await loadAlerts();
    } catch (error) {
      console.error('Erro ao descartar alerta:', error);
      alert('Erro ao descartar alerta. Tente novamente.');
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-100 text-red-800 border-red-300',
      high: 'bg-orange-100 text-orange-800 border-orange-300',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      low: 'bg-green-100 text-green-800 border-green-300'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getSeverityIcon = (severity) => {
    const icons = {
      critical: <AlertTriangle className="w-5 h-5 text-red-600" />,
      high: <AlertCircle className="w-5 h-5 text-orange-600" />,
      medium: <AlertCircle className="w-5 h-5 text-yellow-600" />,
      low: <AlertCircle className="w-5 h-5 text-green-600" />
    };
    return icons[severity] || <AlertCircle className="w-5 h-5 text-gray-600" />;
  };

  const getAlertTypeLabel = (type) => {
    const labels = {
      payment_risk: 'Risco de Inadimplência',
      contract_renewal: 'Renovação de Contrato',
      operational_risk: 'Risco Operacional'
    };
    return labels[type] || type;
  };

  const getAlertTypeIcon = (type) => {
    const icons = {
      payment_risk: <DollarSign className="w-5 h-5" />,
      contract_renewal: <FileText className="w-5 h-5" />,
      operational_risk: <Truck className="w-5 h-5" />
    };
    return icons[type] || <AlertCircle className="w-5 h-5" />;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !stats) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
          <span className="ml-3 text-lg text-gray-600">Carregando alertas...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Alertas Inteligentes</h1>
          <p className="text-gray-600 mt-1">
            Sistema de alertas preditivos com Machine Learning
          </p>
        </div>
        <button
          onClick={generateAlerts}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          Gerar Alertas
        </button>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Total Active Alerts */}
          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Alertas Ativos</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">
                  {stats.active_alerts.total}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <AlertTriangle className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-4 text-sm">
              <span className="text-red-600 font-medium">
                {stats.active_alerts.by_severity.critical} críticos
              </span>
              <span className="text-orange-600 font-medium">
                {stats.active_alerts.by_severity.high} altos
              </span>
            </div>
          </div>

          {/* Payment Risk */}
          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Risco Financeiro</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">
                  {stats.active_alerts.by_type.payment_risk}
                </p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <DollarSign className="w-8 h-8 text-red-600" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-gray-600">
                Impacto: {formatCurrency(stats.metrics.total_predicted_impact)}
              </p>
            </div>
          </div>

          {/* Contract Renewal */}
          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-orange-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Renovações</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">
                  {stats.active_alerts.by_type.contract_renewal}
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <FileText className="w-8 h-8 text-orange-600" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-gray-600">
                Prioridade média: {stats.metrics.avg_priority.toFixed(1)}
              </p>
            </div>
          </div>

          {/* Operational Risk */}
          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Risco Operacional</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">
                  {stats.active_alerts.by_type.operational_risk}
                </p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-full">
                <Truck className="w-8 h-8 text-yellow-600" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-gray-600">
                Confiança média: {(stats.metrics.avg_confidence * 100).toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-800">Filtros</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Alerta
            </label>
            <select
              value={filters.alert_type}
              onChange={(e) => handleFilterChange('alert_type', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">Todos</option>
              <option value="payment_risk">Risco de Inadimplência</option>
              <option value="contract_renewal">Renovação de Contrato</option>
              <option value="operational_risk">Risco Operacional</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Severidade
            </label>
            <select
              value={filters.severity}
              onChange={(e) => handleFilterChange('severity', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">Todas</option>
              <option value="critical">Crítica</option>
              <option value="high">Alta</option>
              <option value="medium">Média</option>
              <option value="low">Baixa</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prioridade Mínima
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={filters.min_priority}
              onChange={(e) => handleFilterChange('min_priority', e.target.value)}
              placeholder="0-100"
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={applyFilters}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Aplicar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Alerts List */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-800">
          Alertas Ativos ({alerts.length})
        </h2>

        {alerts.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <p className="text-xl font-medium text-gray-800 mb-2">
              Nenhum alerta ativo
            </p>
            <p className="text-gray-600">
              O sistema não identificou riscos no momento. Continue monitorando!
            </p>
          </div>
        ) : (
          alerts
            .sort((a, b) => b.priority - a.priority)
            .map((alert) => (
              <div
                key={alert.id}
                className={`bg-white rounded-lg shadow-md border-l-4 p-6 ${
                  alert.severity === 'critical' ? 'border-red-500' :
                  alert.severity === 'high' ? 'border-orange-500' :
                  alert.severity === 'medium' ? 'border-yellow-500' :
                  'border-green-500'
                }`}
              >
                {/* Alert Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gray-100 rounded-lg">
                      {getAlertTypeIcon(alert.alert_type)}
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-800">
                          {alert.title}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getSeverityColor(alert.severity)}`}>
                          {alert.severity.toUpperCase()}
                        </span>
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          {getAlertTypeLabel(alert.alert_type)}
                        </span>
                      </div>
                      <p className="text-gray-600">{alert.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-800 mb-1">
                      {alert.priority}
                    </div>
                    <div className="text-xs text-gray-500">Prioridade</div>
                  </div>
                </div>

                {/* Alert Details */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Confiança do ML</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            alert.confidence_score >= 0.8 ? 'bg-green-500' :
                            alert.confidence_score >= 0.6 ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${alert.confidence_score * 100}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-700">
                        {(alert.confidence_score * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500 mb-1">Impacto Financeiro</p>
                    <p className="text-lg font-semibold text-gray-800">
                      {formatCurrency(alert.predicted_impact_value || 0)}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500 mb-1">Data de Criação</p>
                    <p className="text-sm font-medium text-gray-700">
                      {formatDate(alert.created_at)}
                    </p>
                  </div>
                </div>

                {/* Recommended Actions */}
                {alert.recommended_actions && (
                  <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm font-medium text-blue-800 mb-2">
                      Ações Recomendadas:
                    </p>
                    <p className="text-sm text-blue-700">
                      {alert.recommended_actions}
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center gap-3 pt-4 border-t">
                  <button
                    onClick={() => resolveAlert(alert.id)}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Resolver
                  </button>
                  <button
                    onClick={() => dismissAlert(alert.id)}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  >
                    <XCircle className="w-4 h-4" />
                    Descartar
                  </button>
                </div>
              </div>
            ))
        )}
      </div>
    </div>
  );
};

export default IntelligentAlertsPage;
